import { createContext, useContext } from "react";
import type { ChatMessage, ContactsOfferBlock, CreditConfirmationBlock, DownloadOfferBlock } from "@containers/Hermes/types";

/**
 * An action a message's action bar can open. ChatShell renders its card in
 * place of the composer — one at a time.
 */
export type HermesAction = ({ kind: "bookmark"; block: ContactsOfferBlock } | { kind: "workspace"; block: ContactsOfferBlock; messageId: number } | { kind: "contacts"; block: ContactsOfferBlock; conversationId: string; messageId: number } | { kind: "download"; block: DownloadOfferBlock } | { kind: "credit"; block: CreditConfirmationBlock }) & {
  // The table the action was opened from, when it can be browsed in full.
  source?: ResultsSource;
  // Opened from the bar above a table, on the rows the user ticked. The selection is
  // final: to change it, discard and tick again.
  fromSelection?: boolean;
  // A download of just the ticked rows: records from..to (1-based) of the result.
  initialRange?: { from: number; to: number };
  // A download of exactly these companies (master ids), whichever rows they are.
  companyIds?: string[];
  // A download of exactly these rows (bucket keys), for a table that is not about companies.
  rowKeys?: string[];
};

/**
 * The table an action bar belongs to. `download` carries the spec_token every
 * rows request runs from, so the results panel reads the same query the table,
 * the count and the file use. `previewCount` is how many rows the answer's own
 * table shows — the default selection of an action card.
 */
export type ResultsSource = {
  download: DownloadOfferBlock;
  contacts?: ContactsOfferBlock;
  previewCount: number;
  // The answer the table is in, which contacts requests attach their results to.
  messageId: number;
  // Open the table with nothing selected, for a user choosing from all the records afresh (the "Yes" beneath
  // the bar). Otherwise the rows the answer shows start selected, for one who comes from an action card.
  startEmpty?: boolean;
  // The headers of the answer's preview table, so the full table can be named and ordered like it.
  previewHeaders?: string[];
};

// How a card was opened: from an answer's action bar, or on its own because the
// question asked for that action (see requestedAction). Sent with the card's
// open event (hermes_<action>_opened).
export type ActionTrigger = "action_bar" | "auto_open";

// Lets a message's action bar open an action card, which ChatShell renders in
// place of the composer. Provided by ChatShell; outside it (e.g. the landing
// view) `open` is a no-op and the bars are not shown.
export type ActionPanelContextValue = {
  available: boolean;
  // True while a card is open — suggestions and action bars are disabled until it closes.
  isOpen: boolean;
  open: (action: HermesAction, trigger?: ActionTrigger) => void;
  // Opens the full table beside the chat, to choose from every record of a table.
  openResults: (source: ResultsSource) => void;
};

export const ActionPanelContext = createContext<ActionPanelContextValue>({
  available: false,
  isOpen: false,
  open: () => undefined,
  openResults: () => undefined,
});

export default function useActionPanel(): ActionPanelContextValue {
  return useContext(ActionPanelContext);
}

/**
 * The action an answer asked to open on arrival — a block the backend marked
 * `open_action` because the user's message requested that action ("contacts
 * for Samsung") — or null.
 */
export function requestedAction(message: ChatMessage, conversationId: string | null): HermesAction | null {
  for (const block of message.structured_blocks ?? []) {
    // A priced tariff lookup waiting for the user's go-ahead always opens its card.
    if (block.type === "credit_confirmation" && block.confirm_token) return { kind: "credit", block };
    if (block.type === "contacts_offer" && block.companies?.length) {
      if (block.open_action === "contacts" && conversationId && message.id > 0) return { kind: "contacts", block, conversationId, messageId: message.id };
      if (block.open_action === "bookmark" && block.companies.some((company) => company.master_id)) return { kind: "bookmark", block };
      if (block.open_action === "workspace" && block.companies.some((company) => company.master_id)) return { kind: "workspace", block, messageId: message.id };
    }
    if (block.type === "download_offer" && block.spec_token && block.open_action === "download") return { kind: "download", block };
  }
  return null;
}
